import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled, { keyframes } from 'styled-components'
import AccountTreeRoundedIcon from '@mui/icons-material/AccountTreeRounded'
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded'
import HomeWorkRoundedIcon from '@mui/icons-material/HomeWorkRounded'
import MeetingRoomRoundedIcon from '@mui/icons-material/MeetingRoomRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import { toast } from 'react-toastify'
import { getJumpTargets } from 'services/BpService'
import { line, radius, font, surface, transition, lowerLabel } from 'features/dashboard/tokens'
import { ENTITY_COLORS } from 'constants/entityColors'

export const JUMP_TYPE_META = {
  portfolio: { label: 'Portfolio',        plural: 'Portfolios',         Icon: AccountTreeRoundedIcon, tint: ENTITY_COLORS.portfolio },
  estate:    { label: 'Estate',           plural: 'Estates',            Icon: ApartmentRoundedIcon,   tint: ENTITY_COLORS.estate },
  bp:        { label: 'Building Passport', plural: 'Building Passports', Icon: HomeWorkRoundedIcon,    tint: ENTITY_COLORS.bp },
  unit:      { label: 'Unit Passport',    plural: 'Unit Passports',     Icon: MeetingRoomRoundedIcon, tint: ENTITY_COLORS.unit },
}

const ORDER = { portfolio: 0, estate: 1, bp: 2, unit: 3 }

const rowMeta = item => {
  if (item.type === 'portfolio' || item.type === 'estate') {
    const count = item.bpCount ?? 0
    return `${count} Building Passport${count === 1 ? '' : 's'}`
  }
  if (item.type === 'bp') return item.postcode || null
  if (item.type === 'unit') return item.bpName ? `in ${item.bpName}` : null
  return null
}

const JumpToPicker = ({ open, onClose, onSelect, filterType = null, bpSections = null }) => {
  const [targets, setTargets] = useState(null)
  const [loading, setLoading] = useState(false)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const [sectionsFor, setSectionsFor] = useState(null)
  const [unitsExpanded, setUnitsExpanded] = useState(false)
  const inputRef = useRef(null)
  const fetchedRef = useRef(false)

  useEffect(() => {
    if (!open) return
    setQuery('')
    setActive(0)
    setSectionsFor(null)
    setUnitsExpanded(false)
    if (!fetchedRef.current) {
      fetchedRef.current = true
      setLoading(true)
      getJumpTargets()
        .then(res => setTargets(res.data?.data || []))
        .catch(() => { toast.error('Could not load the list of places to jump to.'); setTargets([]) })
        .finally(() => setLoading(false))
    }
    const t = setTimeout(() => inputRef.current?.focus(), 30)
    return () => clearTimeout(t)
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = e => {
      if (e.key === 'Escape') {
        if (sectionsFor) { setSectionsFor(null); setActive(0) } else onClose?.()
        return
      }
      if (!sectionsFor || !bpSections?.length) return
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive(i => Math.min(i + 1, bpSections.length - 1)) }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(i => Math.max(i - 1, 0)) }
      else if (e.key === 'Enter') {
        e.preventDefault()
        const s = bpSections[active]
        if (s) { onSelect?.({ ...sectionsFor, section: s.key }); onClose?.() }
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose, onSelect, sectionsFor, active, bpSections])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    let list = (targets || []).filter(t => JUMP_TYPE_META[t.type])
    if (filterType) list = list.filter(t => t.type === filterType)
    if (q) list = list.filter(t => (t.name || '').toLowerCase().includes(q))
    return list.sort((a, b) =>
      (ORDER[a.type] ?? 9) - (ORDER[b.type] ?? 9) || (a.name || '').localeCompare(b.name || ''))
  }, [targets, query, filterType])

  useEffect(() => { setActive(0) }, [query, filterType, unitsExpanded])

  const groups = useMemo(() => {
    const byType = {}
    filtered.forEach(t => { (byType[t.type] = byType[t.type] || []).push(t) })
    return Object.keys(byType)
      .sort((a, b) => (ORDER[a] ?? 9) - (ORDER[b] ?? 9))
      .map(type => ({ type, items: byType[type] }))
  }, [filtered])

  const unitsVisible = unitsExpanded || query.trim().length > 0 || Boolean(filterType)
  const visibleItems = useMemo(
    () => filtered.filter(t => t.type !== 'unit' || unitsVisible),
    [filtered, unitsVisible]
  )

  const choose = target => {
    if (!target) return
    if (target.type === 'bp' && bpSections?.length) {
      setSectionsFor(target)
      setActive(0)
      setQuery('')
      return
    }
    onSelect?.(target)
    onClose?.()
  }

  const pickSection = section => {
    if (!section || !sectionsFor) return
    onSelect?.({ ...sectionsFor, section: section.key })
    onClose?.()
  }

  const onKeyDown = e => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(i => Math.min(i + 1, visibleItems.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(i => Math.max(i - 1, 0)) }
    else if (e.key === 'Enter') { e.preventDefault(); choose(visibleItems[active]) }
  }

  if (!open) return null

  let flatIndex = -1

  return createPortal(
    <Backdrop onMouseDown={onClose}>
      <Panel role='dialog' aria-label='Jump to' onMouseDown={e => e.stopPropagation()}>
        {sectionsFor ? (
          <>
            <SectionHeader>
              <BackBtn type='button' onClick={() => { setSectionsFor(null); setActive(0) }} aria-label='Back'>
                <ArrowBackRoundedIcon style={{ fontSize: 18 }} />
              </BackBtn>
              <SectionHeaderText>
                <SectionKicker>Jump to a section of</SectionKicker>
                <SectionBpName>{sectionsFor.name || 'Building Passport'}</SectionBpName>
              </SectionHeaderText>
              <CloseBtn type='button' onClick={onClose} aria-label='Close'>
                <CloseRoundedIcon style={{ fontSize: 18 }} />
              </CloseBtn>
            </SectionHeader>
            <Results>
              {bpSections.map((s, idx) => {
                const Icon = s.Icon
                return (
                  <Row
                    key={s.key}
                    type='button'
                    $active={idx === active}
                    $tint={s.tint}
                    onMouseEnter={() => setActive(idx)}
                    onClick={() => pickSection(s)}
                  >
                    <RowIcon $tint={s.tint}><Icon style={{ fontSize: 16 }} /></RowIcon>
                    <RowText>
                      <RowName>{s.label}</RowName>
                      {s.sub && <RowSub>{s.sub}</RowSub>}
                    </RowText>
                  </Row>
                )
              })}
            </Results>
            <FootHint>↑ ↓ to move · Enter to open · Esc to go back</FootHint>
          </>
        ) : (
        <>
        <SearchRow>
          <SearchRoundedIcon style={{ fontSize: 18, color: '#94a3b8' }} />
          <SearchInput
            ref={inputRef}
            data-tour-id='jumpTo.search'
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={filterType
              ? `Jump to a ${lowerLabel(JUMP_TYPE_META[filterType]?.label || 'place')}…`
              : 'Jump to a portfolio, estate, Building Passport or Unit Passport…'}
          />
          <CloseBtn type='button' onClick={onClose} aria-label='Close'>
            <CloseRoundedIcon style={{ fontSize: 18 }} />
          </CloseBtn>
        </SearchRow>

        {filterType && (
          <FilterNote>
            Showing {lowerLabel(JUMP_TYPE_META[filterType]?.plural || 'entities')} only.
          </FilterNote>
        )}

        <Results data-tour-id='jumpTo.results'>
          {loading ? (
            <SkeletonList aria-hidden='true'>
              {[0, 1, 2, 3, 4].map(i => (
                <SkeletonRow key={i}>
                  <SkeletonDot />
                  <SkeletonBar $w={`${70 - i * 8}%`} />
                </SkeletonRow>
              ))}
            </SkeletonList>
          ) : filtered.length === 0 ? (
            <Empty>
              {query.trim()
                ? `Nothing matches “${query.trim()}”.`
                : 'Nothing to jump to yet.'}
            </Empty>
          ) : (
            groups.map(group => {
              const meta = JUMP_TYPE_META[group.type]
              const collapsible = group.type === 'unit' && !query.trim() && !filterType
              const showItems = !collapsible || unitsExpanded
              return (
                <Group key={group.type}>
                  {collapsible ? (
                    <GroupHeadToggle
                      type='button'
                      onClick={() => setUnitsExpanded(v => !v)}
                      aria-expanded={unitsExpanded}
                    >
                      <GroupChevron>
                        {unitsExpanded
                          ? <ExpandMoreRoundedIcon style={{ fontSize: 16 }} />
                          : <ChevronRightRoundedIcon style={{ fontSize: 16 }} />}
                      </GroupChevron>
                      <GroupLabel>{meta.plural}</GroupLabel>
                      <GroupCount>{group.items.length}</GroupCount>
                      <ShowHint>{unitsExpanded ? 'Hide' : 'Show all'}</ShowHint>
                    </GroupHeadToggle>
                  ) : (
                    <GroupHead>
                      <GroupLabel>{meta.plural}</GroupLabel>
                      <GroupCount>{group.items.length}</GroupCount>
                    </GroupHead>
                  )}
                  {showItems && group.items.map(item => {
                    flatIndex += 1
                    const idx = flatIndex
                    const Icon = meta.Icon
                    const metaText = rowMeta(item)
                    return (
                      <Row
                        key={`${item.type}-${item.id}`}
                        type='button'
                        $active={idx === active}
                        $tint={meta.tint}
                        onMouseEnter={() => setActive(idx)}
                        onClick={() => choose(item)}
                      >
                        <RowIcon $tint={meta.tint}><Icon style={{ fontSize: 16 }} /></RowIcon>
                        <RowText>
                          <RowName>{item.name || 'Untitled'}</RowName>
                          {metaText && <RowSub>{metaText}</RowSub>}
                        </RowText>
                        <RowType $tint={meta.tint}>{meta.label}</RowType>
                      </Row>
                    )
                  })}
                </Group>
              )
            })
          )}
        </Results>

        <FootHint>↑ ↓ to move · Enter to open · Esc to close</FootHint>
        </>
        )}
      </Panel>
    </Backdrop>,
    document.body
  )
}

export default JumpToPicker

const fadeIn = keyframes` from { opacity: 0; } to { opacity: 1; } `
const slideIn = keyframes` from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } `
const pulse = keyframes` 0% { opacity: .5; } 50% { opacity: 1; } 100% { opacity: .5; } `

const Backdrop = styled.div`
  position: fixed; inset: 0;
  z-index: 100100;
  background: rgba(12, 18, 41, 0.42);
  display: flex; align-items: flex-start; justify-content: center;
  padding: 80px 20px 20px;
  animation: ${fadeIn} 140ms ease;
`

const Panel = styled.div`
  width: 100%; max-width: 520px;
  max-height: min(560px, calc(100vh - 120px));
  display: flex; flex-direction: column;
  background: #fff;
  border: 1px solid ${line.base};
  border-radius: ${radius.md};
  box-shadow: 0 24px 60px rgba(12, 18, 41, 0.28);
  overflow: hidden;
  animation: ${slideIn} 160ms ease;
`

const SearchRow = styled.div`
  display: flex; align-items: center; gap: 10px;
  padding: 14px 16px;
  border-bottom: 1px solid ${line.base};
`

const SearchInput = styled.input`
  flex: 1; min-width: 0;
  font: inherit;
  font-size: ${font.emphasis};
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.haiti};
  &::placeholder { color: ${({ theme }) => theme.colors.grey[600]}; }
  &:focus { outline: none; }
`

const CloseBtn = styled.button`
  display: inline-flex; align-items: center; justify-content: center;
  width: 28px; height: 28px;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: ${radius.sm};
  color: ${({ theme }) => theme.colors.grey[600]};
  cursor: pointer;
  transition: ${transition};
  &:hover { background: ${({ theme }) => theme.colors.lighterGrey}; color: ${({ theme }) => theme.colors.haiti}; }
`

const FilterNote = styled.div`
  padding: 8px 16px 0;
  font-size: ${font.caption};
  font-weight: 700;
  letter-spacing: 0.02em;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const SectionHeader = styled.div`
  display: flex; align-items: center; gap: 10px;
  padding: 12px 16px;
  border-bottom: 1px solid ${line.base};
`

const BackBtn = styled.button`
  display: inline-flex; align-items: center; justify-content: center;
  width: 28px; height: 28px;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: ${radius.sm};
  color: ${({ theme }) => theme.colors.grey[600]};
  cursor: pointer;
  transition: ${transition};
  &:hover { background: ${({ theme }) => theme.colors.lighterGrey}; color: ${({ theme }) => theme.colors.haiti}; }
`

const SectionHeaderText = styled.div`
  flex: 1; min-width: 0;
  display: flex; flex-direction: column; gap: 1px;
`

const SectionKicker = styled.span`
  font-size: ${font.caption};
  font-weight: 700;
  letter-spacing: 0.02em;
  color: ${({ theme }) => theme.colors.grey[600]};
`

const SectionBpName = styled.span`
  font-size: ${font.emphasis};
  font-weight: 800;
  color: ${({ theme }) => theme.colors.haiti};
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`

const Results = styled.div`
  flex: 1; min-height: 0;
  overflow-y: auto;
  padding: 8px;
`

const Group = styled.div`
  & + & { margin-top: 4px; }
`

const GroupHead = styled.div`
  display: flex; align-items: center; gap: 6px;
  padding: 8px 10px 4px;
`

const GroupHeadToggle = styled.button`
  font: inherit;
  width: 100%;
  display: flex; align-items: center; gap: 6px;
  padding: 8px 10px 4px;
  border: none;
  background: transparent;
  border-radius: ${radius.sm};
  cursor: pointer;
  text-align: left;
  transition: background-color 120ms ease;
  &:hover { background: ${surface.wash}; }
`

const GroupChevron = styled.span`
  display: inline-flex; align-items: center; justify-content: center;
  margin-left: -4px;
  color: ${({ theme }) => theme.colors.grey[600]};
`

const ShowHint = styled.span`
  margin-left: auto;
  font-size: ${font.caption};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.secondary || '#33becc'};
`

const GroupLabel = styled.span`
  font-size: ${font.caption};
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const GroupCount = styled.span`
  font-size: ${font.caption};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.grey[600]};
  font-variant-numeric: tabular-nums;
`

const Row = styled.button`
  font: inherit;
  width: 100%;
  display: flex; align-items: center; gap: 10px;
  padding: 9px 10px;
  border: none;
  border-radius: ${radius.sm};
  background: ${({ $active }) => $active ? surface.wash : 'transparent'};
  cursor: pointer;
  text-align: left;
  transition: background-color 120ms ease;
  box-shadow: ${({ $active, $tint }) => $active ? `inset 3px 0 0 ${$tint}` : 'none'};
`

const RowIcon = styled.span`
  flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  width: 30px; height: 30px;
  border-radius: ${radius.sm};
  background: ${({ $tint }) => `${$tint}14`};
  color: ${({ $tint }) => $tint};
`

const RowText = styled.span`
  flex: 1; min-width: 0;
  display: flex; flex-direction: column; gap: 1px;
`

const RowName = styled.span`
  min-width: 0;
  font-size: ${font.body};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`

const RowSub = styled.span`
  min-width: 0;
  font-size: ${font.caption};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.grey[600]};
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`

const RowType = styled.span`
  flex-shrink: 0;
  font-size: ${font.caption};
  font-weight: 700;
  color: ${({ $tint }) => $tint};
`

const Empty = styled.div`
  padding: 32px 16px;
  text-align: center;
  font-size: ${font.body};
  color: ${({ theme }) => theme.colors.grey[800]};
`

const FootHint = styled.div`
  padding: 9px 16px;
  border-top: 1px solid ${line.base};
  font-size: ${font.caption};
  color: ${({ theme }) => theme.colors.grey[600]};
  text-align: center;
`

const SkeletonList = styled.div`
  display: flex; flex-direction: column; gap: 4px;
  padding: 8px 2px;
`

const SkeletonRow = styled.div`
  display: flex; align-items: center; gap: 10px;
  padding: 9px 10px;
`

const SkeletonDot = styled.span`
  flex-shrink: 0;
  width: 30px; height: 30px;
  border-radius: ${radius.sm};
  background: ${line.strong};
  animation: ${pulse} 1.4s ease-in-out infinite;
`

const SkeletonBar = styled.span`
  height: 12px;
  width: ${({ $w }) => $w || '60%'};
  border-radius: 4px;
  background: ${line.strong};
  animation: ${pulse} 1.4s ease-in-out infinite;
`
